import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
	INITIAL_STATE_FOR_CREATE_PROJECT,
	PROJECT_TYPES
} from '@features/project-management-tools/constents';
import { isGeneralProjectType, validateProjectForm } from '@features/project-management-tools/utils';
import {
	useCreateProjectMutation,
	useUpdateProjectMutation
} from '@features/project-management-tools/api/projectManagementApi';
import {
	setIsEditProject,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';

import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import CustomDropdown from '@/components/CustomDropdown';
import InputField from '@/components/InputField';
import ModalContainer from '@/components/modals/ModalContainer';
import { useGetAllBrandsQuery, useLazyGetAllUserQuery } from '@/api/commonApis';
import { getErrorMessage, getLoadingState, replaceUnderLine } from '@/utils/Util';
import CommonImage from '@/components/CommonImage';

const InputAndSelectWrapper = ({ children, className }) => {
	return (
		<div className={`col-md-6 pr-2 ${className}`}>
			<div className="form-group">{children}</div>
		</div>
	);
};

const CreateAndEditProjectModal = ({
	onClose = () => {},
	isEdit = false,
	selectedProjectInfo = null,
	onUpdateProjectSuccess
}) => {
	const [projectInfo, setProjectInfo] = useState(null);
	const [errors, setErrors] = useState({});
	const [displayedErrors, setDisplayedErrors] = useState([]);
	const [searchByEmail, setSearchByEmail] = useState('');
	const navigate = useNavigate();

	const dispatch = useDispatch();

	const [createProject, { isLoading: creatingProject, error: projectCreateError }] =
		useCreateProjectMutation();
	const [updateProject, { isLoading: isUpdating, error: updateError }] = useUpdateProjectMutation();
	const [tiggerGetAllUser, { data: allUsers, isLoading: userLoading, error: getUserError }] =
		useLazyGetAllUserQuery();
	const {
		data: allBrands,
		isLoading: brandLoading,
		error: getBrandError
	} = useGetAllBrandsQuery({
		params: { page: 0, size: 1000 }
	});

	useEffect(() => {
		tiggerGetAllUser({ params: { email: searchByEmail, isNitexUser: true, page: 0, size: 30 } });
	}, [searchByEmail]);

	useEffect(() => {
		if (selectedProjectInfo && isEdit) {
			setProjectInfo({
				...selectedProjectInfo,
				projectName: selectedProjectInfo?.name,
				projectType: {
					id: selectedProjectInfo?.ProjectType,
					value: replaceUnderLine(selectedProjectInfo?.ProjectType),
					name: replaceUnderLine(selectedProjectInfo?.ProjectType)
				},
				projectOwner: {
					id: selectedProjectInfo?.ownerResponse?.id,
					value: selectedProjectInfo?.ownerResponse?.name,
					name: selectedProjectInfo?.ownerResponse?.name
				},
				projectStartDate: selectedProjectInfo?.startDate,
				projectEndDate: selectedProjectInfo?.endDate,
				brand: {
					id: selectedProjectInfo?.brandResponse?.id,
					value: selectedProjectInfo?.brandResponse?.name,
					name: selectedProjectInfo?.brandResponse?.name
				},
				buyer1stMeetingDate: selectedProjectInfo?.buyerFirstMeetingDate,
				season: selectedProjectInfo?.season,
				styleCategoryDetails: selectedProjectInfo?.styleCategoryDetails,
				fabricDetails: selectedProjectInfo?.fabricDetails,
				note: selectedProjectInfo?.note
			});
		} else {
			setProjectInfo(INITIAL_STATE_FOR_CREATE_PROJECT);
		}
	}, [selectedProjectInfo, isEdit]);

	const handleChange = (e) => {
		setProjectInfo({ ...projectInfo, [e.target.name]: e.target.value });
		if (e.target.value) setErrors({ ...errors, [e.target.name]: '' });
	};

	const handleSelectChange = (field, value) => {
		setProjectInfo({ ...projectInfo, [field]: value });
		if (value) setErrors({ ...errors, [field]: '' });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const validationErrors = validateProjectForm(projectInfo);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		const requestBody = {
			name: projectInfo?.projectName,
			note: projectInfo?.note,
			ownerId: projectInfo?.projectOwner?.id,
			brandId: projectInfo?.brand?.id,
			projectType: projectInfo?.projectType?.id,
			startDate: projectInfo?.projectStartDate,
			endDate: projectInfo?.projectEndDate,
			buyerFirstMeetingDate: projectInfo?.buyer1stMeetingDate || null,
			season: projectInfo?.season || null,
			styleCategoryDetails: projectInfo?.styleCategoryDetails || null,
			fabricDetails: projectInfo?.fabricDetails || null
		};
		if (isEdit) {
			updateProject({ body: requestBody, id: selectedProjectInfo?.id, sl: selectedProjectInfo?.sl })
				.unwrap()
				.then(() => {
					toastSuccess('Project Updated Successfully!');
					onUpdateProjectSuccess && onUpdateProjectSuccess(projectInfo?.projectStatus);
					setProjectInfo(INITIAL_STATE_FOR_CREATE_PROJECT);
					dispatch(setIsEditProject(false));
					dispatch(setSelectedProjectToEdit(null));
					onClose();
				})
				.catch(({ error }) => {
					toastError(error?.data?.message || 'Something went wrong');
				});
		} else
			createProject({ body: requestBody })
				.unwrap()
				.then((response) => {
					toastSuccess('Project Created Successfully!');
					onUpdateProjectSuccess && onUpdateProjectSuccess('PENDING');
					setProjectInfo(INITIAL_STATE_FOR_CREATE_PROJECT);
					navigate(`/my-project/${response?.id}`);
					onClose();
				})
				.catch(() => {
					// toastError(error?.data?.message || 'Something went wrong');
				});
	};

	// Loading and Error States
	const IS_LOADING = getLoadingState([creatingProject, userLoading, brandLoading, isUpdating]);
	const ERROR_MESSAGES = getErrorMessage([projectCreateError, getUserError, getBrandError, updateError]);

	useEffect(() => {
		if (ERROR_MESSAGES?.length > 0) {
			const newErrors = ERROR_MESSAGES.filter((error) => !displayedErrors.includes(error));
			if (newErrors.length > 0) {
				newErrors.forEach((error) => toastError(error));
				setDisplayedErrors((prev) => [...prev, ...newErrors]);
			}
		}
	}, [ERROR_MESSAGES, displayedErrors]);

	return (
		<LoaderComponent loading={IS_LOADING}>
			<ModalContainer className="create-project-modal">
				<div className="wraper container-fluid dashboard-container">
					<div className="popup-wrapper">
						<form className="common-popup" onSubmit={handleSubmit}>
							<div className="common-popup-header d-flex justify-content-between">
								<div className="popup-tilte">
									<h3 className="semibold-16 mb-0">
										{isEdit ? 'Edit project' : 'Create project'}
									</h3>
								</div>
								<div className="close-btn">
									<CommonImage alt="close" src="/icons/close.svg" onClick={onClose} />
								</div>
							</div>
							<div className="common-popup-body scollable-modal-body scroll-y-label">
								<div className="row">
									{/* Project name */}
									<InputAndSelectWrapper className="col-md-12">
										<InputField
											type="text"
											labelTitle="Project Name*"
											name="projectName"
											value={projectInfo?.projectName}
											onChange={handleChange}
											errors={errors?.projectName}
										/>
									</InputAndSelectWrapper>
									{/* Project type */}
									<InputAndSelectWrapper>
										<label htmlFor="collection">Project type*</label>
										<CustomDropdown
											title="Project type*"
											onItemClick={(value) => handleSelectChange('projectType', value)}
											selectedItem={projectInfo?.projectType?.value}
											items={PROJECT_TYPES}
											toolTipLength={25}
										/>
										{errors?.projectType && (
											<span className="error">{errors?.projectType}</span>
										)}
									</InputAndSelectWrapper>
									{/* Project owner */}
									<InputAndSelectWrapper>
										<label htmlFor="collection">Project Owner*</label>
										<CustomDropdown
											title="Project Owner"
											onItemClick={(value) => handleSelectChange('projectOwner', value)}
											selectedItem={projectInfo?.projectOwner?.value}
											items={allUsers?.map((user) => ({
												...user,
												value: user?.name
											}))}
											onItemSearch={(value) => setSearchByEmail(value)}
										/>
										{errors?.projectOwner && (
											<span className="error">{errors?.projectOwner}</span>
										)}
									</InputAndSelectWrapper>
									{/* Project start date */}
									<InputAndSelectWrapper>
										<InputField
											type="date"
											labelTitle="Project start date*"
											name="projectStartDate"
											value={projectInfo?.projectStartDate}
											onChange={handleChange}
											errors={errors?.projectStartDate}
										/>
									</InputAndSelectWrapper>
									{/* Project end date */}
									<InputAndSelectWrapper>
										<InputField
											type="date"
											labelTitle="Project end date*"
											name="projectEndDate"
											value={projectInfo?.projectEndDate}
											onChange={handleChange}
											errors={errors?.projectEndDate}
										/>
									</InputAndSelectWrapper>
									{/* Brand */}
									{!isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper>
											<label htmlFor="collection">Brand*</label>
											<CustomDropdown
												title="Brand"
												onItemClick={(value) => handleSelectChange('brand', value)}
												selectedItem={projectInfo?.brand?.value}
												items={allBrands}
											/>
											{errors?.brand && <span className="error">{errors?.brand}</span>}
										</InputAndSelectWrapper>
									)}
									{/* Buyer 1st meeting date */}
									{!isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper>
											<label htmlFor="collection">Buyer 1st meeting date</label>
											<InputField
												type="date"
												name="buyer1stMeetingDate"
												value={projectInfo?.buyer1stMeetingDate}
												onChange={handleChange}
												errors={errors?.buyer1stMeetingDate}
											/>
										</InputAndSelectWrapper>
									)}
									{/* Season */}
									{!isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper className="col-md-12">
											<InputField
												type="text"
												labelTitle="Season*"
												name="season"
												value={projectInfo?.season}
												onChange={handleChange}
												errors={errors?.season}
											/>
										</InputAndSelectWrapper>
									)}
									{/* Style category details (Optional) */}
									{!isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper className="col-md-12">
											<label htmlFor="collection">
												Style category details (Optional)
											</label>
											<textarea
												name="styleCategoryDetails"
												id="styleCategoryDetails"
												placeholder="description"
												value={projectInfo?.styleCategoryDetails}
												onChange={handleChange}
											/>
										</InputAndSelectWrapper>
									)}
									{/* Fabric details (Optional) */}
									{!isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper className="col-md-12">
											<label htmlFor="collection">Fabric details (Optional)</label>
											<textarea
												name="fabricDetails"
												id="fabricDetails"
												placeholder="description"
												value={projectInfo?.fabricDetails}
												onChange={handleChange}
											/>
										</InputAndSelectWrapper>
									)}

									{/* Fabric details (Optional) */}
									{isGeneralProjectType(projectInfo?.projectType?.id) && (
										<InputAndSelectWrapper className="col-md-12">
											<label htmlFor="collection">Note (optional)</label>
											<textarea
												name="note"
												id="note"
												placeholder="description"
												value={projectInfo?.note}
												onChange={handleChange}
											/>
										</InputAndSelectWrapper>
									)}
								</div>
							</div>
							<div className="modal-footer">
								<div className="footer-buttons text-right">
									<button className="button outline size32 ml-2" onClick={onClose}>
										Cancel
									</button>
									<button className="button size32 ml-2" type="submit">
										Submit
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default CreateAndEditProjectModal;
